import {createContext, use, type ReactNode} from 'react'
import type {AhpSpaceSelection as ContractAhpSpaceSelection} from '@github-ui/ahp-session-client/contracts'

import type {ReasoningEffort} from '../utils/reasoning-effort'

export interface ModelUpdateOptions {
  reconcile?: boolean
}

export type OnUpdateModel = (
  modelId: string,
  supportedReasoningEfforts?: readonly string[],
  options?: ModelUpdateOptions,
) => void

export interface ModelPickerState {
  selectedModelId: string | undefined
  onUpdateModel: OnUpdateModel
}

export type OnUpdateReasoningEffort = (
  reasoningEffort: ReasoningEffort | undefined,
  supportedReasoningEfforts?: readonly string[],
  options?: ModelUpdateOptions,
) => void

export interface ReasoningEffortPickerState {
  selectedReasoningEffort: ReasoningEffort | undefined
  onUpdateReasoningEffort: OnUpdateReasoningEffort
}

export type AhpSpaceSelection = ContractAhpSpaceSelection

export type OnSelectSpace = (space: AhpSpaceSelection | undefined) => void

export interface SpacePickerState {
  selectedSpace: AhpSpaceSelection | undefined
  onSelectSpace: OnSelectSpace
}

export interface EstablishedAhpComposerProviderProps {
  children: ReactNode
  model?: ModelPickerState
  reasoningEffort?: ReasoningEffortPickerState
  space?: SpacePickerState
}

const ModelSelectionContext = createContext<string | undefined>(undefined)
const ModelUpdateContext = createContext<OnUpdateModel | null>(null)
const ReasoningEffortSelectionContext = createContext<ReasoningEffort | undefined>(undefined)
const ReasoningEffortUpdateContext = createContext<OnUpdateReasoningEffort | null>(null)
const SpaceSelectionContext = createContext<AhpSpaceSelection | undefined>(undefined)
const SpaceUpdateContext = createContext<OnSelectSpace | null>(null)

export function EstablishedAhpComposerProvider({
  children,
  model,
  reasoningEffort,
  space,
}: EstablishedAhpComposerProviderProps) {
  return (
    <ModelUpdateContext value={model?.onUpdateModel ?? null}>
      <ModelSelectionContext value={model?.selectedModelId}>
        <ReasoningEffortUpdateContext value={reasoningEffort?.onUpdateReasoningEffort ?? null}>
          <ReasoningEffortSelectionContext value={reasoningEffort?.selectedReasoningEffort}>
            <SpaceUpdateContext value={space?.onSelectSpace ?? null}>
              <SpaceSelectionContext value={space?.selectedSpace}>{children}</SpaceSelectionContext>
            </SpaceUpdateContext>
          </ReasoningEffortSelectionContext>
        </ReasoningEffortUpdateContext>
      </ModelSelectionContext>
    </ModelUpdateContext>
  )
}

export function useModelPicker(): ModelPickerState {
  const onUpdateModel = use(ModelUpdateContext)
  if (!onUpdateModel) throw new Error('useModelPicker must be used within EstablishedAhpComposerProvider')
  return {selectedModelId: use(ModelSelectionContext), onUpdateModel}
}

export function useReasoningEffortPicker(): ReasoningEffortPickerState {
  const onUpdateReasoningEffort = use(ReasoningEffortUpdateContext)
  if (!onUpdateReasoningEffort) {
    throw new Error('useReasoningEffortPicker must be used within EstablishedAhpComposerProvider')
  }
  return {
    selectedReasoningEffort: use(ReasoningEffortSelectionContext),
    onUpdateReasoningEffort,
  }
}

export function useSpacePicker(): SpacePickerState {
  const onSelectSpace = use(SpaceUpdateContext)
  if (!onSelectSpace) {
    throw new Error('useSpacePicker must be used within EstablishedAhpComposerProvider')
  }
  return {
    selectedSpace: use(SpaceSelectionContext),
    onSelectSpace,
  }
}

EstablishedAhpComposerProvider.displayName = 'EstablishedAhpComposerProvider'
